// Course (series) registry.
//
// A "course" is a multipage series layered on top of the normal doc system:
// each chapter is an ordinary markdown doc rendered by MarkdownPage, and the
// manifest below defines the title, order, and which chapters are live. This
// keeps chapter ordering as one source of truth and lets both the course hub
// (CoursePage) and the chapter pages (MarkdownPage/BreadCrumb) share the same
// prev/next/index logic instead of each re-deriving it.
//
// Today there is a single live course (the gated template). The shape here is
// written so additional courses can be added without changing consumers — add
// another manifest import and push it into COURSES below.
//
// Note: the DQA handbook (chapters.json) is intentionally NOT registered here.
// It stays as its original standalone content; the course machinery only drives
// courses listed in COURSES.

import courseTemplate from '@/assets/data/course-template.json';
import stillYourself from '@/assets/data/still-yourself.json';
import mindset from '@/assets/data/mindset.json';
import library from '@/assets/data/library.json';

export type CourseChapter = {
  id: number;
  docId?: number;
  tag: string;
  title: string;
  description: string;
  slug: string;
  route: string;
  contentFile: string;
  published: boolean;
};

export type Course = {
  slug: string;
  title: string;
  subtitle: string;
  /** When true, the hub is gated behind the course access secret. */
  locked: boolean;
  /** What one entry is called in "Chapter 3 of 8". A collection of essays says "Essay". */
  unit: string;
  entries: CourseChapter[];
};

export type ChapterContext = {
  course: Course;
  /** Published chapters only, in order. */
  chapters: CourseChapter[];
  /** Index of the current chapter within `chapters`. */
  index: number;
  current: CourseChapter;
  prev: CourseChapter | null;
  next: CourseChapter | null;
};

function libraryDescription(slug: string): string {
  const entry = ((library as any).entries || []).find((l: any) => l.slug === slug);
  return entry?.description ?? '';
}

function normalizeCourse(raw: any): Course {
  const entries: CourseChapter[] = ((raw?.entries as any[]) || []).map((e) => ({
    id: e.id,
    docId: e.docId,
    tag: e.tag ?? '',
    title: e.title ?? '',
    // An entry without its own description borrows the article's library
    // description, so a collection of existing articles doesn't repeat them.
    description: e.description ?? libraryDescription(e.slug),
    slug: e.slug ?? '',
    route: e.route ?? (e.slug ? `/doc/${e.slug}` : ''),
    contentFile: e.contentFile ?? '',
    // Treat a chapter as published unless it is explicitly false, so older
    // manifests without the flag keep working.
    published: e.published !== false,
  }));

  return {
    slug: raw?.slug ?? '',
    title: raw?.title ?? '',
    subtitle: raw?.subtitle ?? '',
    locked: raw?.locked === true,
    unit: raw?.unit ?? 'Chapter',
    entries,
  };
}

const COURSES: Course[] = [
  normalizeCourse(courseTemplate),
  normalizeCourse(stillYourself),
  normalizeCourse(mindset),
];

export function getAllCourses(): Course[] {
  return COURSES;
}

// Whether a collection is listed in the Library (it has an entry there), so
// the breadcrumb can show Library above it. The course template isn't.
export function isLibraryCollection(slug: string): boolean {
  if (!slug) return false;
  return ((library as any).entries || []).some(
    (e: any) => e.slug === slug && e.published !== false && !e.private
  );
}

export function getCourseBySlug(slug: string): Course | undefined {
  if (!slug) return undefined;
  return COURSES.find((c) => c.slug === slug);
}

/** The first course — convenience for the single-course `/Course` route. */
export function getDefaultCourse(): Course | undefined {
  return COURSES[0];
}

/** Published chapters of a course, in manifest order. */
export function getPublishedChapters(course: Course): CourseChapter[] {
  return course.entries.filter((e) => e.published && e.slug);
}

/**
 * Given a doc slug, return its course context (course, ordered published
 * chapters, position, and prev/next neighbours) — or null if the slug is not a
 * chapter of any course. prev/next are computed over published chapters only.
 */
export function getChapterContext(docSlug: string): ChapterContext | null {
  if (!docSlug) return null;

  for (const course of COURSES) {
    const chapters = getPublishedChapters(course);
    const index = chapters.findIndex((c) => c.slug === docSlug);
    if (index === -1) continue;

    return {
      course,
      chapters,
      index,
      current: chapters[index],
      prev: index > 0 ? chapters[index - 1] : null,
      next: index < chapters.length - 1 ? chapters[index + 1] : null,
    };
  }

  return null;
}
